<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CatalogAPI from 'dashboard/api/catalog';

const props = defineProps({
  currency: {
    type: String,
    default: 'BRL',
  },
  // Quando true, posiciona relativo ao elemento pai (modo painel).
  // Quando false (padrão), usa fixed no centro inferior (modo chat).
  anchored: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();

const allProducts = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const searchQuery = ref('');
const searchQuerySent = ref('');
const currentPage = ref(1);
const hasMore = ref(false);
const searchDebounce = ref(null);
const searchInputRef = ref(null);

const catalogVariants = computed(() => {
  return allProducts.value.flatMap(p =>
    (p.variants || [{}]).map(v => ({
      localId: v.id,
      externalId: v.external_id,
      title: p.title,
      variantTitle: p.variants.length === 1 ? null : v.title,
      imageUrl: v.image_url || p.image_url,
      sku: v.sku,
      price: parseFloat(v.price || 0),
      retailerId: v.meta_retailer_id,
      sourceKind: p.source?.kind,
    }))
  );
});

const fetchProducts = async (query = '', page = 1, append = false) => {
  try {
    if (page === 1) loading.value = true;
    else loadingMore.value = true;

    const response = await CatalogAPI.getProducts({ query, page });
    const newProducts = response.data.products || [];

    if (append) {
      allProducts.value = [...allProducts.value, ...newProducts];
    } else {
      allProducts.value = newProducts;
    }

    currentPage.value = response.data.page || page;
    hasMore.value = response.data.has_more || false;
  } catch {
    if (!append) allProducts.value = [];
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

fetchProducts();

const onSearchInput = event => {
  const query = event.target.value;
  searchQuery.value = query;
  clearTimeout(searchDebounce.value);
  searchDebounce.value = setTimeout(() => {
    searchQuerySent.value = query;
    fetchProducts(query, 1, false);
  }, 300);
};

const onScroll = event => {
  const el = event.target;
  if (
    el.scrollTop + el.clientHeight >= el.scrollHeight - 20 &&
    hasMore.value &&
    !loadingMore.value
  ) {
    fetchProducts(searchQuerySent.value, currentPage.value + 1, true);
  }
};

const formatCurrency = amount => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(amount);
};

const thumbUrl = url => {
  if (!url) return url;
  return url.includes('?') ? `${url}&width=80` : url;
};

const selectVariant = variant => {
  emit('select', variant);
};

watch(
  () => searchInputRef.value,
  () => {
    nextTick(() => searchInputRef.value?.focus());
  }
);
</script>

<template>
  <OnClickOutside @trigger="$emit('close')">
    <div
      class="z-[200] w-80 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
      :class="anchored ? 'absolute bottom-full left-0 mb-1' : 'fixed'"
      :style="anchored ? {} : { bottom: '80px', left: '50%', transform: 'translateX(-50%)' }"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          ref="searchInputRef"
          :value="searchQuery"
          :placeholder="t('CATALOG.PRODUCT_POPOVER.SEARCH_PLACEHOLDER')"
          class="w-full text-sm px-2 py-1.5 rounded border border-n-weak outline-none focus:outline-n-brand bg-transparent"
          @input="onSearchInput"
        />
      </div>

      <div class="overflow-y-auto max-h-52" @scroll="onScroll">
        <div
          v-if="loading && catalogVariants.length === 0"
          class="flex items-center justify-center py-6"
        >
          <Spinner />
        </div>

        <div
          v-else-if="catalogVariants.length === 0"
          class="px-3 py-4 text-sm text-n-slate-11 text-center"
        >
          {{ t('CATALOG.PRODUCT_POPOVER.EMPTY') }}
        </div>

        <template v-else>
          <button
            v-for="v in catalogVariants"
            :key="v.localId"
            class="w-full flex items-center gap-2 px-3 py-2 hover:bg-n-alpha-2 text-left"
            @click="selectVariant(v)"
          >
            <img
              v-if="v.imageUrl"
              :src="thumbUrl(v.imageUrl)"
              :alt="v.title"
              class="size-8 rounded object-cover shrink-0"
            />
            <div
              v-else
              class="size-8 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
            >
              <span class="i-lucide-image text-n-slate-10 text-xs" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-n-slate-12 truncate">{{ v.title }}</p>
              <p
                v-if="v.variantTitle"
                class="text-xs text-n-slate-11 truncate"
              >
                {{ v.variantTitle }}
              </p>
            </div>
            <span class="text-xs font-medium text-n-slate-11 shrink-0">
              {{ formatCurrency(v.price) }}
            </span>
          </button>
        </template>

        <div
          v-if="loadingMore"
          class="flex items-center justify-center py-2"
        >
          <Spinner />
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>
